import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import STRINGS from '../i18n';
import DISHES from '../mock';
import ALWAYS from '../data_always';
import DELI from '../data_deli';
import FRIDGES_MOCK from '../data_fridges';
import api from '../api';

const AppContext = createContext(null);

const BUNDLED = [...DISHES, ...ALWAYS, ...DELI];

export function AppProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem('ac_lang') || 'fr');
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ac_cart') || '[]'); } catch { return []; }
  });
  const [dishes, setDishes] = useState(BUNDLED);
  const [fridges, setFridges] = useState(FRIDGES_MOCK);
  const [settings, setSettings] = useState({});
  // Which weeks the two menu tabs show and whether next week can still be
  // ordered — decided by the server, so the tabs follow the rotation and the
  // "Commande" buttons cannot outlive the cutoff.
  const [menuState, setMenuState] = useState({ open: true, dates: [], week1Monday: '', week2Monday: '', paymentMode: 'twint_qr' });
  const [loading, setLoading] = useState(true);
  const [usingApi, setUsingApi] = useState(false);
  const [token, setToken] = useState(() => localStorage.getItem('ac_token') || '');
  // Set when a stale basket had to be pruned, so the page can explain why
  // something the customer put there has gone.
  const [cartNotice, setCartNotice] = useState(null);
  // The basket drawer, so any page can open it without navigating away.
  const [cartOpen, setCartOpen] = useState(false);

  // Company client session. Kept in localStorage so a partner company stays
  // signed in between visits; the token is what makes the extended catalogue
  // and the company collection points appear, and the server decides that —
  // the browser only carries the token.
  const [companyToken, setCompanyToken] = useState(() => localStorage.getItem('ac_company_token') || '');
  const [company, setCompany] = useState(() => {
    try { return JSON.parse(localStorage.getItem('ac_company') || 'null'); } catch { return null; }
  });

  useEffect(() => { localStorage.setItem('ac_lang', lang); }, [lang]);
  useEffect(() => { localStorage.setItem('ac_cart', JSON.stringify(cart)); }, [cart]);
  useEffect(() => {
    if (companyToken) localStorage.setItem('ac_company_token', companyToken);
    else localStorage.removeItem('ac_company_token');
    if (company) localStorage.setItem('ac_company', JSON.stringify(company));
    else localStorage.removeItem('ac_company');
  }, [companyToken, company]);

  const loadData = useCallback(async () => {
    try {
      const [d, f, s, m] = await Promise.all([
        api.getDishes(), api.getFridges(), api.getSettings(),
        api.getPreorderWindow().catch(() => null),
      ]);
      if (Array.isArray(d) && d.length) { setDishes(d); setUsingApi(true); }
      if (Array.isArray(f) && f.length) setFridges(f);
      if (s) setSettings(s);
      if (m && typeof m.open === 'boolean') setMenuState(m);
    } catch (e) {
      // fallback to bundled mock (offline / API down)
      console.warn('API unavailable, using bundled data', e?.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // A basket lives in localStorage indefinitely, but the menu rotates every
  // week. Left alone, a basket from last week keeps ids that have moved into
  // the fridge or been archived: the header counts them, the checkout renders
  // nothing for them, and the server then rejects the whole order for an item
  // the customer cannot see to remove. Prune on every menu load.
  //
  // Only ever against real data — pruning against the bundled offline menu
  // would empty a perfectly good basket whenever the API hiccuped.
  useEffect(() => {
    if (!usingApi || loading || cart.length === 0) return;
    const kept = [];
    const movedToFridge = [];
    const noLongerListed = [];
    cart.forEach((item) => {
      const dish = dishes.find((d) => d.id === item.id);
      if (!dish) { noLongerListed.push(item); return; }
      if (dish.availability !== 'week2') { movedToFridge.push({ ...item, name: dish.name }); return; }
      kept.push(item);
    });
    if (movedToFridge.length === 0 && noLongerListed.length === 0) return;
    setCart(kept);
    setCartNotice({ movedToFridge, noLongerListed });
  }, [dishes, usingApi, loading, cart]);

  // The cutoff is a server setting, not a constant — it moved from Wednesday
  // 18:00 to Thursday 09:00 once already. Every customer-facing sentence that
  // names it uses {cutoff} and gets this, so the next change is a setting
  // change and not a hunt through the translations.
  const cutoffLabel = useMemo(() => {
    const iso = menuState && menuState.nextCutoff;
    if (!iso) return lang === 'fr' ? 'la clôture hebdomadaire' : 'the weekly cutoff';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return lang === 'fr' ? 'la clôture hebdomadaire' : 'the weekly cutoff';
    if (lang === 'fr') {
      const day = d.toLocaleDateString('fr-CH', { weekday: 'long' });
      const hh = d.getHours();
      const mm = d.getMinutes();
      return `${day} ${hh}h${mm ? String(mm).padStart(2, '0') : '00'}`;
    }
    const day = d.toLocaleDateString('en-GB', { weekday: 'long' });
    return `${day} ${d.toLocaleTimeString('en-GB', { hour: 'numeric', minute: '2-digit', hour12: true }).replace(':00', '')}`;
  }, [menuState, lang]);

  const t = useCallback((key) => {
    const s = STRINGS[key];
    const raw = s ? (s[lang] || s.fr) : key;
    return typeof raw === 'string' && raw.includes('{cutoff}')
      ? raw.replace(/\{cutoff\}/g, cutoffLabel)
      : raw;
  }, [lang, cutoffLabel]);

  const tf = useCallback((obj) => {
    if (!obj) return '';
    if (typeof obj === 'string') return obj;
    return obj[lang] || obj.fr || '';
  }, [lang]);

  const getDish = useCallback((id) => dishes.find((d) => d.id === id), [dishes]);

  const addToCart = useCallback((id, qty = 1) => {
    setCart((prev) => {
      const found = prev.find((i) => i.id === id);
      if (found) return prev.map((i) => i.id === id ? { ...i, qty: i.qty + qty } : i);
      return [...prev, { id, qty }];
    });
  }, []);

  const updateQty = useCallback((id, qty) => {
    setCart((prev) => qty <= 0 ? prev.filter((i) => i.id !== id) : prev.map((i) => i.id === id ? { ...i, qty } : i));
  }, []);

  const removeFromCart = useCallback((id) => setCart((prev) => prev.filter((i) => i.id !== id)), []);
  const clearCart = useCallback(() => setCart([]), []);

  const cartCount = cart.reduce((a, i) => a + i.qty, 0);
  const cartTotal = cart.reduce((a, i) => {
    const d = getDish(i.id); return a + (d ? d.price * i.qty : 0);
  }, 0);

  const login = useCallback(async (password) => {
    const res = await api.login(password);
    localStorage.setItem('ac_token', res.token);
    setToken(res.token);
    return res.token;
  }, []);

  // Also ends the session server-side. Clearing the browser alone left the
  // token valid until it expired.
  const logout = useCallback(() => {
    api.logout();
    localStorage.removeItem('ac_token');
    setToken('');
  }, []);

  const companyLogin = useCallback(async (code) => {
    const res = await api.companyLogin(code);
    localStorage.setItem('ac_company_token', res.token);
    setCompanyToken(res.token);
    setCompany(res.company);
    await loadData();          // the menu widens the moment the session opens
    return res.company;
  }, [loadData]);

  const companyLogout = useCallback(async () => {
    try { await api.companyLogout(); } catch (e) { /* the session dies anyway */ }
    localStorage.removeItem('ac_company_token');
    localStorage.removeItem('ac_company');
    setCompanyToken(''); setCompany(null);
    // Company-only items may be sitting in the basket; reloading the menu makes
    // the existing pruning drop them rather than leaving unorderable rows.
    await loadData();
  }, [loadData]);

  // Sessions live in the server's memory, so a redeploy ends them. Check once
  // on load and clear a stale one rather than showing a company a catalogue
  // that will refuse its order.
  useEffect(() => {
    if (!companyToken) return;
    api.companyMe()
      .then((c) => setCompany(c))
      .catch(() => {
        localStorage.removeItem('ac_company_token');
        localStorage.removeItem('ac_company');
        setCompanyToken(''); setCompany(null);
      });
  }, [companyToken]);

  const value = {
    lang, setLang, t, tf,
    dishes, fridges, settings, menuState, loading, usingApi,
    getDish, reload: loadData,
    cart, addToCart, updateQty, removeFromCart, clearCart, cartCount, cartTotal,
    cartNotice, dismissCartNotice: () => setCartNotice(null),
    cartOpen, setCartOpen, cutoffLabel,
    token, isAdmin: !!token, login, logout,
    company, companyToken, isCompany: !!company, companyLogin, companyLogout,
  };
  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
}

export default AppContext;
